import styled, { css } from 'styled-components';

import { TWidgetIframeParentDimension } from '@lib/core/service/types/interface';

import {
  TFontStyle,
  TFontType,
  TStyleWeight,
  TTextAlight,
  TTextDecoration,
  TTextTransform,
  TTypographySizes,
  TTypographyVariantSize,
  TTypographyVariants,
} from '@components/web/src/foundations/Text/types';
import { SCREEN_SIZE } from '@components/web/src/styles';

const fontWeights: Record<TStyleWeight, number> = {
  bold: 700,
  medium: 500,
  normal: 400,
  semibold: 600,
};

export const TYPOGRAPHY_SIZES: TTypographyVariantSize = {
  body1: {
    kiosk: {
      'font-size': '16px',
      'line-height': '24px',
    },
    l: {
      'font-size': '16px',
      'line-height': '24px',
    },
    md: {
      'font-size': '14px',
      'line-height': '18px',
    },
    sm: {
      'font-size': '14px',
      'line-height': '18px',
    },
  },
  body2: {
    kiosk: {
      'font-size': '14px',
      'line-height': '20px',
    },
    l: {
      'font-size': '14px',
      'line-height': '20px',
    },
    md: {
      'font-size': '12px',
      'line-height': '16px',
    },
    sm: {
      'font-size': '12px',
      'line-height': '16px',
    },
  },
  body3: {
    kiosk: {
      'font-size': '12px',
      'line-height': '18px',
    },
    l: {
      'font-size': '12px',
      'line-height': '18px',
    },
    md: {
      'font-size': '10px',
      'line-height': '14px',
    },
    sm: {
      'font-size': '10px',
      'line-height': '14px',
    },
  },
  h1: {
    kiosk: {
      'font-size': '80px',
      'letter-spacing': '-1.6px',
      'line-height': '110px',
    },
    l: {
      'font-size': '80px',
      'letter-spacing': '-1.6px',
      'line-height': '110px',
    },
    md: {
      'font-size': '60px',
      'letter-spacing': '-1.2px',
      'line-height': '72px',
    },
    sm: {
      'font-size': '48px',
      'letter-spacing': '-0.96px',
      'line-height': '60px',
    },
  },
  h2: {
    kiosk: {
      'font-size': '60px',
      'letter-spacing': '-1.2px',
      'line-height': '72px',
    },
    l: {
      'font-size': '60px',
      'letter-spacing': '-1.2px',
      'line-height': '72px',
    },
    md: {
      'font-size': '48px',
      'letter-spacing': '-0.96px',
      'line-height': '60px',
    },
    sm: {
      'font-size': '42px',
      'letter-spacing': '-0.84px',
      'line-height': '48px',
    },
  },
  h3: {
    kiosk: {
      'font-size': '48px',
      'letter-spacing': '-0.96px',
      'line-height': '60px',
    },
    l: {
      'font-size': '48px',
      'letter-spacing': '-0.96px',
      'line-height': '60px',
    },
    md: {
      'font-size': '42px',
      'letter-spacing': '-0.84px',
      'line-height': '48px',
    },
    sm: {
      'font-size': '36px',
      'letter-spacing': '-0.72px',
      'line-height': '42px',
    },
  },
  h4: {
    kiosk: {
      'font-size': '40px',
      'letter-spacing': '-0.8px',
      'line-height': '48px',
    },
    l: {
      'font-size': '36px',
      'letter-spacing': '-0.72px',
      'line-height': '44px',
    },
    md: {
      'font-size': '30px',
      'letter-spacing': '-0.6px',
      'line-height': '36px',
    },
    sm: {
      'font-size': '30px',
      'letter-spacing': '-0.6px',
      'line-height': '36px',
    },
  },
  h5: {
    kiosk: {
      'font-size': '28px',
      'letter-spacing': '-0.56px',
      'line-height': '32px',
    },
    l: {
      'font-size': '30px',
      'line-height': '38px',
    },
    md: {
      'font-size': '24px',
      'line-height': '30px',
    },
    sm: {
      'font-size': '24px',
      'line-height': '30px',
    },
  },
  h6: {
    kiosk: {
      'font-size': '22px',
      'line-height': '28px',
    },
    l: {
      'font-size': '24px',
      'line-height': '32px',
    },
    md: {
      'font-size': '20px',
      'line-height': '24px',
    },
    sm: {
      'font-size': '20px',
      'line-height': '24px',
    },
  },
  subtitle1: {
    kiosk: {
      'font-size': '18px',
      'line-height': '28px',
    },
    l: {
      'font-size': '20px',
      'line-height': '30px',
    },
    md: {
      'font-size': '18px',
      'line-height': '24px',
    },
    sm: {
      'font-size': '18px',
      'line-height': '24px',
    },
  },
  subtitle2: {
    kiosk: {
      'font-size': '18px',
      'line-height': '28px',
    },
    l: {
      'font-size': '18px',
      'line-height': '28px',
    },
    md: {
      'font-size': '16px',
      'line-height': '20px',
    },
    sm: {
      'font-size': '16px',
      'line-height': '20px',
    },
  },
};

export const generateTypographyStyles = (
  fontSize = 'body3',
  fontType: TFontType,
  fontWeight = 'normal',
  isKioskRootElementType = false,
  retailerFonts,
  dimension: TWidgetIframeParentDimension,
) => {
  const fontFamily =
    fontType === 'grace' ? retailerFonts?.grace?.family || 'Fraunces' : retailerFonts?.noGrace?.family || 'Montserrat';

  const getFontTypography = (size: TTypographySizes) => css`
    font-family: ${fontFamily};
    font-weight: ${fontWeights[fontWeight]};
    font-size: ${TYPOGRAPHY_SIZES[fontSize][size]['font-size']};
    line-height: ${TYPOGRAPHY_SIZES[fontSize][size]['line-height']};
    letter-spacing: ${TYPOGRAPHY_SIZES[fontSize][size]?.['letter-spacing'] || 'normal'};
  `;

  if (isKioskRootElementType) return getFontTypography('kiosk');

  if (dimension) {
    if (dimension === 'mobile') {
      return css`
        ${getFontTypography('sm')}
      `;
    }
    if (dimension === 'tablet') {
      return css`
        ${getFontTypography('md')}
      `;
    }

    if (dimension === 'desktop') {
      return css`
        ${getFontTypography('l')}
      `;
    }
  }

  return css`
    ${getFontTypography('sm')}

    @container rootContainer (min-width: ${SCREEN_SIZE.TABLET_WIDTH}) {
      ${getFontTypography('md')}
    }

    @container rootContainer (min-width: ${SCREEN_SIZE.DESKTOP_WIDTH}) {
      ${getFontTypography('l')}
    }
  `;
};

export const StyledText = styled.span<{
  $size?: TTypographyVariants;
  $weight?: TStyleWeight;
  $color?: string;
  $fontType?: TFontType;
  $linesLimit?: number;
  $textDecoration?: TTextDecoration;
  $fontStyle?: TFontStyle;
  $textAlign?: TTextAlight;
  $textTransform?: TTextTransform;
  $strongColor?: string;
  $strongFontStyle?: TFontStyle;
  $strongSize?: TTypographyVariants;
  $strongWeight?: TStyleWeight;
  $dimension?: TWidgetIframeParentDimension;
}>`
  p {
    margin: 0;
  }

  color: ${({ $color = 'inherit' }) => $color};
  font-style: ${({ $fontStyle = 'normal' }) => $fontStyle};
  text-decoration: ${({ $textDecoration = 'none' }) => $textDecoration};
  text-align: ${({ $textAlign = 'unset' }) => $textAlign};
  text-transform: ${({ $textTransform = 'none' }) => $textTransform};

  ${({ $size, $weight, $fontType, $dimension, theme }) =>
    generateTypographyStyles(
      $size,
      $fontType,
      $weight,
      theme?.isKioskRootElementType,
      theme?.retailerFonts,
      $dimension,
    )}

  ${({ $linesLimit }) =>
    $linesLimit &&
    css`
      text-overflow: ellipsis;
      overflow: hidden;
      overflow-wrap: anywhere;

      display: -webkit-box !important;
      -webkit-line-clamp: ${$linesLimit};
      -webkit-box-orient: vertical;
      white-space: normal;
    `}
  ${({ $textDecoration }) =>
    $textDecoration &&
    css`
      text-decoration: ${$textDecoration};
    `}
  strong {
    color: ${({ $strongColor = 'inherit' }) => $strongColor};
    font-style: ${({ $strongFontStyle = 'inherit' }) => $strongFontStyle};
    ${({ $strongSize, $strongWeight, $fontType, $dimension, theme }) =>
      generateTypographyStyles(
        $strongSize,
        $fontType,
        $strongWeight,
        theme?.isKioskRootElementType,
        theme?.retailerFonts,
        $dimension,
      )}
  }
`;
